import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
	CalendarIcon,
	MapPinIcon,
	CheckCircleIcon,
	ClockIcon,
	UserIcon
} from '@heroicons/react/24/outline';
import { getPositionInfo, getAvailableSlotsByPosition, bookSlotByPosition } from '../services/aiRecruiterService';
import { resolveLogoUrl } from '../utils/urlUtils';
import { useBrandingColors } from '../hooks/useBrandingColors';
import {
	PERSONAL_DATA_CONSENT_AGREEMENT_URL,
	PERSONAL_DATA_CONSENT_TEXT,
	PERSONAL_DATA_POLICY_URL
} from '../constants/personalDataConsent';

// Конвертация времени из московской таймзоны (UTC+3) в локальную таймзону клиента
const MOSCOW_OFFSET_HOURS = 3; // Москва = UTC+3

const convertMoscowTimeToLocal = (timeStr: string, dateStr: string): string => {
	const [hours, minutes] = timeStr.split(':').map(Number);
	const [year, month, day] = dateStr.split('-').map(Number);
	const utcDate = new Date(Date.UTC(year, month - 1, day, hours - MOSCOW_OFFSET_HOURS, minutes));
	const localHours = utcDate.getHours().toString().padStart(2, '0');
	const localMinutes = utcDate.getMinutes().toString().padStart(2, '0');
	return `${localHours}:${localMinutes}`;
};

const getTimezoneOffsetFromMoscow = (): number => {
	const now = new Date();
	const localOffsetMinutes = now.getTimezoneOffset();
	const moscowOffsetMinutes = -MOSCOW_OFFSET_HOURS * 60;
	return (moscowOffsetMinutes - localOffsetMinutes) / 60;
};

const VacancyPage: React.FC = () => {
	const { positionId } = useParams<{ positionId: string }>();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token');
	const recr = searchParams.get('recr');

	const [vacancy, setVacancy] = useState<any>(null);
	const [loading, setLoading] = useState(true);
	const [tokenError, setTokenError] = useState<string | null>(null);
	const [personalData, setPersonalData] = useState({
		fullName: '',
		phone: '',
		consent: false
	});
	const [consentGiven, setConsentGiven] = useState(false);
	const [availableSlots, setAvailableSlots] = useState<string[]>([]);
	const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
	const [selectedDate, setSelectedDate] = useState<string | null>(null);
	const [bookingLoading, setBookingLoading] = useState(false);
	const [bookingSuccess, setBookingSuccess] = useState(false);
	const [brandingLogoUrl, setBrandingLogoUrl] = useState<string | undefined>(undefined);
	// Состояния для брендинга из API (для публичных страниц)
	const [brandingPrimaryColor, setBrandingPrimaryColor] = useState<string | null>(null);
	const [brandingSecondaryColor, setBrandingSecondaryColor] = useState<string | null>(null);
	// Используем цвета из API (если есть), иначе fallback на default
	const colors = useBrandingColors(brandingPrimaryColor, brandingSecondaryColor);

	const workFormatMap: Record<string, string> = {
		'REMOTE': 'Удаленная работа',
		'OFFICE': 'Офис',
		'HYBRID': 'Гибридный формат'
	};

	const scheduleMap: Record<string, string> = {
		'FLEXIBLE': 'Гибкий график',
		'FULL_DAY': 'Полный день',
		'FULL_TIME': 'Полный день',
		'PART_TIME': 'Частичная занятость',
		'SHIFT': 'Сменный график'
	};

	const employmentMap: Record<string, string> = {
		'OFFICIAL': 'Официальное оформление',
		'CONTRACT': 'Договор ГПХ',
		'FREELANCE': 'Фриланс',
		'INTERNSHIP': 'Стажировка'
	};

	useEffect(() => {
		loadPosition();
	}, [positionId]);

	// НЕ подписываемся на brandingLogoFromStore для публичной страницы
	// Логотип должен браться из API (vacancy.logoUrl), а не из кэша авторизованного пользователя

	useEffect(() => {
		if (consentGiven && selectedDate) {
			loadAvailableSlotsData(selectedDate);
		}
	}, [consentGiven, selectedDate]);

	useEffect(() => {
		const root = document.documentElement;
		root.style.setProperty('--brand-primary', colors.primary);
		root.style.setProperty('--brand-primary-hover', colors.primaryHover);
	}, [colors.primary, colors.primaryHover]);

	const loadPosition = async () => {
		if (!positionId) return;

		try {
			// Загружаем данные позиции из business-back через ai-recruiter-back
			const data = await getPositionInfo(positionId, token || undefined);
			setVacancy(data);

			// Устанавливаем брендинг из API-ответа (для публичных страниц)
			if (data.primaryColor) {
				setBrandingPrimaryColor(data.primaryColor);
			}
			if (data.secondaryColor) {
				setBrandingSecondaryColor(data.secondaryColor);
			}
			if (data.logoUrl) {
				const url = resolveLogoUrl(data.logoUrl) || data.logoUrl;
				setBrandingLogoUrl(url);
			}

			// Pre-fill form if token provided candidate data
			if (data.candidateName || data.candidatePhone) {
				setPersonalData({
					fullName: data.candidateName || '',
					phone: data.candidatePhone || '',
					consent: false
				});
			}

			// Check if token already used
			if (data.registrationUsed) {
				setTokenError('Эта ссылка уже была использована для регистрации');
			}
		} catch (error: any) {
			console.error('Failed to load position:', error);
			if (error.response?.status === 404) {
				setTokenError('Ссылка недействительна или устарела');
			}
		} finally {
			setLoading(false);
		}
	};

	const loadAvailableSlotsData = async (date: string) => {
		if (!positionId) return;

		try {
			const slots = await getAvailableSlotsByPosition(positionId, date, recr);
			setAvailableSlots(slots);
		} catch (error) {
			console.error('Failed to load slots:', error);
			setAvailableSlots([]);
		}
	};

	const handleConsentSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (personalData.consent && personalData.fullName && personalData.phone) {
			setConsentGiven(true);
			const nextWorkday = (() => {
				const d = new Date();
				d.setDate(d.getDate() + 1);
				while (d.getDay() === 0 || d.getDay() === 6) {
					d.setDate(d.getDate() + 1);
				}
				return d;
			})();
			setSelectedDate(`${nextWorkday.getFullYear()}-${String(nextWorkday.getMonth() + 1).padStart(2, '0')}-${String(nextWorkday.getDate()).padStart(2, '0')}`);
		}
	};

	const handleSlotBooking = async () => {
		if (bookingLoading || !selectedSlot || !selectedDate || !positionId) return;

		setBookingLoading(true);
		try {
			await bookSlotByPosition(positionId, {
				date: selectedDate,
				time: selectedSlot,
				candidateName: personalData.fullName,
				candidatePhone: personalData.phone,
				personalDataConsent: personalData.consent,
				token: token || undefined,
				recr: recr || undefined
			});
			setBookingSuccess(true);
		} catch (error: any) {
			console.error('Failed to book slot:', error);
			if (error.response?.data?.message) {
				alert(error.response.data.message);
			} else {
				alert('Не удалось забронировать время. Попробуйте еще раз.');
			}
		} finally {
			setBookingLoading(false);
		}
	};

	const getAvailableDates = () => {
		const dates: Date[] = [];
		const today = new Date();
		for (let i = 1; i <= 7; i++) {
			const date = new Date(today);
			date.setDate(today.getDate() + i);
			const day = date.getDay();
			if (day === 0 || day === 6) continue;
			dates.push(date);
		}
		return dates;
	};

	const formatDate = (date: string) => {
		const [year, month, day] = date.split('-').map(Number);
		const localDate = new Date(year, month - 1, day);
		const options: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' };
		return localDate.toLocaleDateString('ru-RU', options);
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-[#f5f6f1] flex items-center justify-center">
				<div className="flex flex-col items-center gap-4">
					<div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
				</div>
			</div>
		);
	}

	if (!vacancy || tokenError) {
		return (
			<div className="min-h-screen bg-[#F5F6F1] flex items-center justify-center p-4">
				<div className="bg-white rounded-[16px] border p-8 shadow-sm border border-gray-100 max-w-md text-center">
					<div className="text-red-600 text-xl font-semibold mb-2">
						{tokenError || 'Вакансия не найдена'}
					</div>
					<p className="text-gray-600">
						{tokenError ? 'Пожалуйста, свяжитесь с рекрутером для получения новой ссылки.' : 'Проверьте правильность ссылки'}
					</p>
				</div>
			</div>
		);
	}

	if (bookingSuccess) {
		return (
			<div className="min-h-screen bg-[#F5F6F1] flex items-center justify-center p-4">
				<div className="bg-white rounded-[16px] shadow-sm p-8 max-w-md w-full text-center border border-gray-100">
					<CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
					<h2 className="text-2xl font-bold text-gray-900 mb-2">Успешно записаны!</h2>
					<p className="text-gray-600">
						Вы записаны на интервью {formatDate(selectedDate!)} в {convertMoscowTimeToLocal(selectedSlot!, selectedDate!)}.
						Мы свяжемся с вами по телефону {personalData.phone} для подтверждения.
					</p>
				</div>
			</div>
		);
	}

	const resolvedLogoSrc = (() => {
		if (vacancy?.logoUrl) return resolveLogoUrl(vacancy.logoUrl) || vacancy.logoUrl;
		if (brandingLogoUrl) return brandingLogoUrl;
		return undefined;
	})();

	// Parse skills from skillGroups JSON if present
	const skills: string[] = (() => {
		if (!vacancy.skillGroups) return [];
		try {
			const parsed = JSON.parse(vacancy.skillGroups);
			if (Array.isArray(parsed)) {
				return parsed.flatMap((group: any) => {
					if (typeof group === 'string') return [group];
					if (group?.skills && Array.isArray(group.skills)) return group.skills.map((s: any) => s?.name || s);
					return [];
				}).filter(Boolean);
			}
		} catch { /* ignore */ }
		return [];
	})();

	return (
		<div className="min-h-screen bg-[#F5F6F1] py-12 px-4 font-sans text-gray-900">
			<div className="max-w-5xl mx-auto space-y-6">
				{/* Vacancy Card */}
				<div className="bg-white rounded-[16px] shadow-sm p-8 md:p-10">
					{/* Top Section: Logo + Content */}
					<div className="flex flex-col md:flex-row gap-6 items-start mb-8">
						{/* Logo */}
						{resolvedLogoSrc && (
							<div className="flex-shrink-0 w-[64px] h-[64px] md:w-[72px] md:h-[72px] flex items-center justify-center">
								<img
									src={resolvedLogoSrc}
									alt={vacancy.company || 'Company Logo'}
									className="w-full h-full object-contain"
								/>
							</div>
						)}

						{/* Right Content */}
						<div className="flex flex-col gap-3 w-full">
							{/* Badges Row */}
							<div className="flex items-center gap-2">
								<span className="bg-[#EFFAF6] text-[#38C793] rounded-[6px] px-2 py-1 text-[16px] leading-6 font-normal">
									Активна
								</span>
								<span className="rounded-[6px] px-2 py-1 text-[16px] leading-6 font-normal" style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}>
									Срочно
								</span>
							</div>

							{/* Title */}
							<h1 className="text-[24px] font-medium leading-8 text-[#0A0D14]">
								{vacancy.name}
							</h1>

							{/* Chips */}
							<div className="flex flex-wrap gap-2">
								{vacancy.schedule && (
									<div className="bg-[#F5F6F1] rounded-[8px] h-[36px] px-[10px] flex items-center gap-[6px] text-[#525866] text-[16px]">
										<ClockIcon className="w-4 h-4" />
										{scheduleMap[vacancy.schedule] || vacancy.schedule}
									</div>
								)}
								{vacancy.level && (
									<div className="bg-[#F5F6F1] rounded-[8px] h-[36px] px-[10px] flex items-center gap-[6px] text-[#525866] text-[16px]">
										<UserIcon className="w-4 h-4" />
										{vacancy.level}
									</div>
								)}
								{vacancy.workFormat && (
									<div className="bg-[#F5F6F1] rounded-[8px] h-[36px] px-[10px] flex items-center gap-[6px] text-[#525866] text-[16px]">
										<MapPinIcon className="w-4 h-4" />
										{workFormatMap[vacancy.workFormat] || vacancy.workFormat}
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Info Grid */}
					<div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
						{vacancy.schedule && (
							<div className="bg-[#F5F6F1] rounded-[12px] p-3 flex items-center gap-3">
								<div className="w-10 h-10 bg-white rounded-[10px] flex items-center justify-center flex-shrink-0">
									<ClockIcon className="w-5 h-5 text-[#525866]" />
								</div>
								<div className="flex flex-col">
									<span className="text-[#868C98] text-[16px] leading-6">График работы</span>
									<span className="text-[#0A0D14] text-[16px] leading-6">
										{scheduleMap[vacancy.schedule] || vacancy.schedule}
									</span>
								</div>
							</div>
						)}


						{vacancy.workFormat && (
							<div className="bg-[#F5F6F1] rounded-[12px] p-3 flex items-center gap-3">
								<div className="w-10 h-10 bg-white rounded-[10px] flex items-center justify-center flex-shrink-0">
									<MapPinIcon className="w-5 h-5 text-[#525866]" />
								</div>
								<div className="flex flex-col">
									<span className="text-[#868C98] text-[16px] leading-6">Формат работы</span>
									<span className="text-[#0A0D14] text-[16px] leading-6">
										{workFormatMap[vacancy.workFormat] || vacancy.workFormat}
									</span>
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Content Section */}
				<div className="bg-white rounded-[16px] shadow-sm p-8 md:p-10 space-y-10">
					{/* About Company */}
					{vacancy.companyDescription && (
						<section>
							<h2 className="text-lg font-bold text-gray-900 mb-4">О компании</h2>
							<div
								className="text-gray-600 leading-relaxed text-base"
								dangerouslySetInnerHTML={{ __html: vacancy.companyDescription }}
							/>
						</section>
					)}

					{vacancy.companyDescription && vacancy.description && <hr className="border-gray-100" />}

					{/* Description */}
					{vacancy.description && (
						<section>
							<h2 className="text-lg font-bold text-gray-900 mb-4">Описание вакансии</h2>
							<div
								className="text-gray-600 leading-relaxed text-base [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-1 [&>p]:mb-3"
								dangerouslySetInnerHTML={{ __html: vacancy.description }}
							/>
						</section>
					)}

					{/* Соц. пакет */}
					{vacancy.socialPackageText && (
						<>
							<hr className="border-gray-100" />
							<section>
								<h2 className="text-lg font-bold text-gray-900 mb-4">Что входит в соц. пакет</h2>
								<div className="text-gray-600 leading-relaxed text-base whitespace-pre-line">
									{vacancy.socialPackageText}
								</div>
							</section>
						</>
					)}

				{/* Skills */}
					{skills.length > 0 && (
						<>
							<hr className="border-gray-100" />
							<section>
								<h2 className="text-lg font-bold text-gray-900 mb-4">Ключевые навыки</h2>
								<div className="flex flex-wrap gap-2">
									{skills.map((skill: string, index: number) => (
										<span
											key={index}
											className="px-3 py-1.5 rounded-[16px] text-sm font-medium"
											style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}
										>
											{skill}
										</span>
									))}
								</div>
							</section>
						</>
					)}
				</div>

				{/* Form Section */}
				<div className="bg-white rounded-[16px] shadow-sm p-8 md:p-10">
					<>
						{!consentGiven ? (
							<div>
								<h2 className="text-xl font-bold text-gray-900 mb-6">
									Записаться на интервью
								</h2>
								<form onSubmit={handleConsentSubmit} className="space-y-5">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1.5">
											ФИО
										</label>
										<input
											type="text"
											placeholder="Иван Иванов"
											value={personalData.fullName}
											onChange={(e) => setPersonalData({ ...personalData, fullName: e.target.value })}
											className="w-full px-4 py-3 border border-gray-200 rounded-[16px] focus:outline-none transition-all"
											onFocus={(e) => { e.currentTarget.style.borderColor = colors.primary; }}
											onBlur={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
											required
										/>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1.5">
											Телефон
										</label>
										<input
											type="tel"
											placeholder="+7 (999) 123-45-67"
											value={personalData.phone}
											onChange={(e) => setPersonalData({ ...personalData, phone: e.target.value })}
											className="w-full px-4 py-3 border border-gray-200 rounded-[16px] focus:outline-none transition-all"
											onFocus={(e) => { e.currentTarget.style.borderColor = colors.primary; }}
											onBlur={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
											required
										/>
									</div>

									<div className="flex items-start gap-3 pt-2">
										<input
											type="checkbox"
											id="consent"
											checked={personalData.consent}
											onChange={(e) => setPersonalData({ ...personalData, consent: e.target.checked })}
											className="mt-1 w-5 h-5 rounded-[16px]"
											style={{ accentColor: colors.primary }}
											required
										/>
										<div className="text-sm text-gray-500 leading-relaxed">
											<label htmlFor="consent" className="cursor-pointer">
												{PERSONAL_DATA_CONSENT_TEXT}
											</label>
											<div className="mt-1 flex flex-wrap gap-x-2 gap-y-1">
												<a
													href={PERSONAL_DATA_CONSENT_AGREEMENT_URL}
													target="_blank"
													rel="noopener noreferrer"
													className="underline"
												>
													Согласие на обработку персональных данных
												</a>
												<a
													href={PERSONAL_DATA_POLICY_URL}
													target="_blank"
													rel="noopener noreferrer"
													className="underline"
												>
													Политика обработки персональных данных
												</a>
											</div>
										</div>
									</div>

									<button
										type="submit"
										disabled={!personalData.consent || !personalData.fullName || !personalData.phone}
										className="w-full py-4 text-white font-semibold rounded-[16px] transition-colors disabled:opacity-70 disabled:cursor-not-allowed mt-4"
										style={{
											backgroundColor: colors.primary,
										}}
										onMouseEnter={(e) => {
											if (!e.currentTarget.disabled) {
												e.currentTarget.style.backgroundColor = colors.primaryHover;
											}
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.backgroundColor = colors.primary;
										}}
									>
										Продолжить
									</button>
								</form>
							</div>
						) : (
							<div>
								<h2 className="text-xl font-bold text-gray-900 mb-6">
									Выберите удобное время
								</h2>

								<div className="mb-8">
									<h3 className="text-sm font-medium text-gray-700 mb-3">Выберите дату</h3>
									<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
										{getAvailableDates().map(date => {
											const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
											const isSelected = selectedDate === dateStr;
											return (
												<button
													key={dateStr}
													onClick={() => {
														setSelectedDate(dateStr);
														setSelectedSlot(null);
														loadAvailableSlotsData(dateStr);
													}}
													type="button"
													className={`p-4 rounded-[12px] border transition-all ${
														isSelected
															? 'border-gray-200'
															: 'border-gray-200 hover:bg-gray-50'
													}`}
													style={isSelected ? {
														borderColor: colors.primary,
														backgroundColor: `${colors.primary}15`,
														color: colors.primary
													} : {}}
													onMouseEnter={(e) => {
														if (!isSelected) {
															e.currentTarget.style.borderColor = colors.primary;
														}
													}}
													onMouseLeave={(e) => {
														if (!isSelected) {
															e.currentTarget.style.borderColor = '';
														}
													}}
												>
													<div className="text-sm opacity-80 mb-1">
														{date.toLocaleDateString('ru-RU', { weekday: 'short' })}
													</div>
													<div className="text-xl font-semibold">
														{date.getDate()} {date.toLocaleDateString('ru-RU', { month: 'short' })}
													</div>
												</button>
											);
										})}
									</div>
								</div>

								{selectedDate && (
									<div className="mb-8">
										<h3 className="text-sm font-medium text-gray-700 mb-3">
											Выберите время
											{getTimezoneOffsetFromMoscow() !== 0 && (
												<span className="ml-2 text-xs text-gray-500 font-normal">
													(ваше локальное время)
												</span>
											)}
										</h3>
										{availableSlots.length > 0 ? (
											<div className="grid grid-cols-3 md:grid-cols-6 gap-3">
												{availableSlots.map(slot => {
													const isSelected = selectedSlot === slot;
													const localTime = convertMoscowTimeToLocal(slot, selectedDate);
													return (
														<button
															key={slot}
															onClick={() => setSelectedSlot(slot)}
															type="button"
															className={`p-3 rounded-[12px] border transition-all ${
																isSelected
																	? 'border-gray-200'
																	: 'border-gray-200 hover:bg-gray-50'
															}`}
															style={isSelected ? {
																borderColor: colors.primary,
																backgroundColor: `${colors.primary}15`,
																color: colors.primary
															} : {}}
															onMouseEnter={(e) => {
																if (!isSelected) {
																	e.currentTarget.style.borderColor = colors.primary;
																}
															}}
															onMouseLeave={(e) => {
																if (!isSelected) {
																	e.currentTarget.style.borderColor = '';
																}
															}}
														>
															{localTime}
														</button>
													);
												})}
											</div>
										) : (
											<div className="bg-gray-50 rounded-[12px] p-4 text-center text-gray-500">
												Нет доступных слотов на выбранную дату
											</div>
										)}
									</div>
								)}

								{selectedSlot && (
									<div className="border-t border-gray-100 pt-6">
										<div className="mb-6 p-4 rounded-[16px] flex items-center gap-3" style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}>
											<CheckCircleIcon className="w-6 h-6" />
											<p className="font-medium">
												{formatDate(selectedDate!)} в {convertMoscowTimeToLocal(selectedSlot, selectedDate!)}
											</p>
										</div>
										<button
											onClick={handleSlotBooking}
											disabled={bookingLoading}
											className="w-full py-4 text-white font-semibold rounded-[16px] transition-colors disabled:opacity-70"
											style={{ backgroundColor: colors.primary }}
											onMouseEnter={(e) => {
												if (!e.currentTarget.disabled) {
													e.currentTarget.style.backgroundColor = colors.primaryHover;
												}
											}}
											onMouseLeave={(e) => {
												e.currentTarget.style.backgroundColor = colors.primary;
											}}
										>
											{bookingLoading ? 'Бронирование...' : 'Забронировать время'}
										</button>
									</div>
								)}
							</div>
						)}
					</>
				</div>
			</div>
		</div>
	);
};

export default VacancyPage;
